import { Role } from '../types';

export const downloadCsvTemplate = (headers: string[], filename: string): void => {
  const csvContent = headers.join(',') + '\n';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const parseCsvToUsers = async (file: File, expectedHeaders: string[]): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvText = event.target?.result as string;
        const lines = csvText.trim().replace(/\r\n/g, '\n').split('\n');

        if (lines.length < 1 || !lines[0].trim()) {
          throw new Error(`CSV file appears to be empty or missing headers.`);
        }

        const headers = lines[0]
          .split(',')
          .map(h => h.trim().toLowerCase().replace(/"/g, ''));

        const missingHeaders = expectedHeaders.filter(eh => !headers.includes(eh.toLowerCase()));
        if (missingHeaders.length > 0) {
          throw new Error(`CSV is missing required headers: ${missingHeaders.join(', ')}. Found: ${headers.join(', ')}`);
        }

        if (lines.length < 2) {
          resolve([]);
          return;
        }

        const users = lines
          .slice(1)
          .map((line, index) => {
            if (!line.trim()) return null;

            const values = (line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [])
              .map(v => v.trim().replace(/"/g, ''));

            const userObject: { [key: string]: string | number } = { rowNumber: index + 2 };
            headers.forEach((header, i) => {
              userObject[header] = values[i] || '';
            });
            return userObject;
          })
          .filter(Boolean);

        resolve(users as any[]);
      } catch (error: any) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read the file."));
    };

    reader.readAsText(file);
  });
};

export const parseCsvToUsersStrict = async (
  file: File,
  expectedHeaders: string[]
): Promise<any[]> => {
  const users = await parseCsvToUsers(file, expectedHeaders);

  const first = users?.[0];
  if (!first) return users;

  const expected = new Set(expectedHeaders.map(h => String(h).trim().toLowerCase()));
  const actual = Object.keys(first)
    .map(k => String(k).trim().toLowerCase())
    .filter(k => k !== 'rownumber');

  const extras = actual.filter(h => !expected.has(h));
  if (extras.length) {
    throw new Error(`CSV has unexpected headers: ${extras.join(', ')}. Allowed: ${expectedHeaders.join(', ')}`);
  }

  const missing = Array.from(expected).filter(h => !actual.includes(h));
  if (missing.length) {
    throw new Error(`CSV missing required headers: ${missing.join(', ')}. Allowed: ${expectedHeaders.join(', ')}`);
  }

  return users;
};
